import { Button, Text } from "@shopify/polaris"
import confetti from "canvas-confetti"
import { observer } from "mobx-react"
import { useCallback, useEffect, useRef } from "react"

import stores from "@/stores"

const SummaryItem = (props: { title: string; description: string }) => {
  const { title, description } = props

  return (
    <div className="flex items-center gap-2">
      <span className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-[#1274ff] text-xs text-white">
        ✓
      </span>
      <div>
        <Text as="p" variant="bodyMd">
          {title}
        </Text>
        <Text as="p" tone="subdued" variant="bodyMd">
          {description}
        </Text>
      </div>
    </div>
  )
}

const StepFour = observer(
  (props: { isSaving: boolean; onBack: () => void; onFinish: () => void }) => {
    const { isSaving, onBack, onFinish } = props
    const onboarding = stores.OnboardingStore
    const containerRef = useRef<HTMLDivElement>(null)

    const solutionCount = onboarding.selectedSolutions.length
    const refundCount = onboarding.selectedSolutions.filter((item) =>
      [1, 7, 2, 4].includes(item.value),
    ).length
    const exchangeCount = solutionCount - refundCount

    const fireConfetti = useCallback(() => {
      if (!containerRef.current) return

      const rect = containerRef.current.getBoundingClientRect()
      const leftX = rect.left / window.innerWidth
      const rightX = rect.right / window.innerWidth
      const centerY = rect.top / window.innerHeight

      const colors = [
        "#ef4444",
        "#f59e0b",
        "#8b5cf6",
        "#3b82f6",
        "#ec4899",
        "#fbbf24",
      ]

      const duration = 300
      const end = Date.now() + duration

      ;(function frame() {
        confetti({
          particleCount: 5,
          angle: 50,
          spread: 100,
          origin: { x: leftX - 0.1, y: centerY + 0.1 },
          colors: colors,
        })
        confetti({
          particleCount: 5,
          angle: 130,
          spread: 100,
          origin: { x: rightX + 0.1, y: centerY + 0.1 },
          colors: colors,
        })
        if (Date.now() < end) {
          requestAnimationFrame(frame)
        }
      })()
    }, [])

    useEffect(() => {
      const timer = setTimeout(() => {
        fireConfetti()
      }, 300)

      return () => {
        clearTimeout(timer)
      }
    }, [fireConfetti])

    return (
      <div
        ref={containerRef}
        id="step4"
        className=" flex flex-col items-center gap-5 pt-8 text-center"
      >
        <div className="text-[44px] leading-none">🎉</div>
        <div>
          <Text as="h1" alignment="center" variant="headingXl">
            {t("YouReAllSet")}
          </Text>
          <Text as="p" alignment="center" tone="subdued" variant="bodyMd">
            {t("YourStoreIsReadyForReturns")}
          </Text>
        </div>
        <div className="summary-card grid w-full grid-cols-1 gap-4 rounded-xl border border-solid border-[var(--p-color-border)] bg-white p-5 text-left shadow-100 md:grid-cols-2">
          <SummaryItem
            description={t("XReasons", {
              x: onboarding.selectedReasons.length,
            })}
            title={t("ReturnReasons")}
          />
          <SummaryItem
            description={t("WithinXDaysAfterOrderFulfillmentDate", {
              x: onboarding.returnDays,
            })}
            title={t("ReturnWindow")}
          />
          <SummaryItem
            description={
              solutionCount
                ? t("ExchangeXAndRefundY", { x: exchangeCount, y: refundCount })
                : "Not configured"
            }
            title={t("ReturnSolutions")}
          />
          <SummaryItem
            description={
              onboarding.logoUrl ? t("BrandedAndReady") : t("DefaultBranding")
            }
            title={t("ReturnPortal")}
          />
        </div>
        <div
          className="flex w-full flex-col justify-center rounded-2xl px-8 py-4 text-left text-white"
          style={{
            backgroundImage: `url('https://return-cdn.parcelpanel.net/public/guide/new-guide-banner-bg.png')`,
            backgroundSize: "100% 100%",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <Text as="p" variant="headingSm">
            {t("Start14DayFreeTrialTodayNoPaymentReq")}
          </Text>
          <p className="mt-1 text-[13px] leading-5 text-white/80">
            {t("AfterTrialUpgradeContinueFreePlan")}
          </p>
        </div>
        <div className="flex flex-col items-center gap-4">
          <div className="blueButton goDashboard">
            <Button
              loading={isSaving}
              onClick={onFinish}
              size="large"
              variant="tertiary"
            >
              <Text as="p" variant="headingLg">
                {t("GoToDashboard")}
              </Text>
            </Button>
          </div>
          <Button disabled={isSaving} onClick={onBack} variant="tertiary">
            {t("Back_0")}
          </Button>
        </div>
      </div>
    )
  },
)

export default StepFour
